"use client"

import { useState, useEffect, useMemo, useRef } from "react"
import { ref, onValue, remove, set } from "../lib/firebase-database"
import { db } from "../firebase"
import { Layout } from "./Layout"
import { Loader2, Trash2, Plus, X, Search, GripVertical } from "lucide-react"
import { useAuth } from "../hooks/useAuth"
import { useNavigate } from "react-router-dom"

export default function PromotedVillas() {
  const { user, isAdmin } = useAuth()
  const navigate = useNavigate()
  const [promotedRows, setPromotedRows] = useState([]) // { id (PK), villaId, position }
  const [availableVillas, setAvailableVillas] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [showAddVilla, setShowAddVilla] = useState(false)
  const [savingOrder, setSavingOrder] = useState(false)
  const dragFromIdx = useRef(null)
  const [dragOverIdx, setDragOverIdx] = useState(null)

  useEffect(() => {
    if (!user || !isAdmin) {
      navigate("/")
      return
    }
    setIsLoading(true)
    setError(null)
    const promotedRef = ref(db, "promotedVillas")
    const villasRef = ref(db, "villas")

    onValue(
      promotedRef,
      (snap) => {
        const data = snap.val()
        if (data) {
          const list = Object.entries(data)
            .map(([id, villa], index) => ({
              id,
              villaId: villa.villaId || id,
              position: villa.position || index + 1,
              addedAt: villa.addedAt,
            }))
            .sort((a, b) => a.position - b.position)
          setPromotedRows(list)
        } else {
          setPromotedRows([])
        }
      },
      (err) => setError(`Failed to load promoted villas: ${err.message}`)
    )

    onValue(
      villasRef,
      (snap) => {
        const data = snap.val()
        setAvailableVillas(
          data
            ? Object.entries(data).map(([id, villa]) => ({ id, ...villa }))
            : []
        )
        setIsLoading(false)
      },
      (err) => {
        setError(`Failed to load villas: ${err.message}`)
        setIsLoading(false)
      }
    )
  }, [user, isAdmin, navigate])

  // Merge promoted rows with full villa records
  const promotedVillas = useMemo(() => {
    if (availableVillas.length === 0) return promotedRows
    const byId = new Map(availableVillas.map((v) => [v.id, v]))
    return promotedRows.map((row) => {
      const villa = byId.get(row.villaId) || {}
      return { ...villa, ...row }
    })
  }, [promotedRows, availableVillas])

  const writePosition = async (row, position) => {
    await set(ref(db, `promotedVillas/${row.id}`), {
      villaId: row.villaId,
      position,
      addedAt: row.addedAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    })
  }

  const persistOrder = async (rows) => {
    setSavingOrder(true)
    try {
      for (const r of rows) {
        await writePosition(r, r.position)
      }
    } catch (err) {
      setError(`Failed to update positions: ${err.message}`)
    } finally {
      setSavingOrder(false)
    }
  }

  const handleRemoveVilla = async (row) => {
    if (!window.confirm("Remove this villa from the promoted list?")) return
    try {
      await remove(ref(db, `promotedVillas/${row.id}`))
      const remaining = promotedRows
        .filter((r) => r.id !== row.id)
        .map((r, idx) => ({ ...r, position: idx + 1 }))
      setPromotedRows(remaining)
      persistOrder(remaining)
    } catch (err) {
      setError(`Failed to remove villa: ${err.message}`)
    }
  }

  const handleAddVilla = async (villa) => {
    try {
      const position = promotedRows.length + 1
      await set(ref(db, `promotedVillas/${villa.id}`), {
        villaId: villa.id,
        position,
        addedAt: new Date().toISOString(),
      })
      // Optimistic local update so it appears instantly
      setPromotedRows((prev) => [
        ...prev,
        {
          id: villa.id,
          villaId: villa.id,
          position,
          addedAt: new Date().toISOString(),
        },
      ])
      setShowAddVilla(false)
      setSearchQuery("")
    } catch (err) {
      setError(`Failed to add villa: ${err.message}`)
    }
  }

  const moveTo = (fromIdx, toIdx) => {
    if (
      fromIdx == null ||
      toIdx == null ||
      fromIdx === toIdx ||
      fromIdx < 0 ||
      toIdx < 0 ||
      fromIdx >= promotedRows.length ||
      toIdx >= promotedRows.length
    ) {
      return
    }
    const arr = [...promotedRows]
    const [moved] = arr.splice(fromIdx, 1)
    arr.splice(toIdx, 0, moved)
    const updated = arr.map((r, idx) => ({ ...r, position: idx + 1 }))
    setPromotedRows(updated)
    persistOrder(updated)
  }

  const handlePositionChange = (rowId, newPosition) => {
    const fromIdx = promotedRows.findIndex((r) => r.id === rowId)
    if (fromIdx === -1) return
    const target = Number.parseInt(newPosition, 10)
    if (Number.isNaN(target)) return
    const toIdx = Math.max(
      0,
      Math.min(promotedRows.length - 1, target - 1)
    )
    moveTo(fromIdx, toIdx)
  }

  const filteredAvailable = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    const promotedIds = new Set(promotedRows.map((r) => r.villaId))
    return availableVillas
      .filter((v) => !promotedIds.has(v.id))
      .filter((v) =>
        !q
          ? true
          : v.name?.toLowerCase().includes(q) ||
            v.owner?.name?.toLowerCase().includes(q)
      )
  }, [availableVillas, promotedRows, searchQuery])

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Promoted Villas
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              The order set here is shown exactly the same on the homepage.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setShowAddVilla(!showAddVilla)
              setSearchQuery("")
            }}
            className="inline-flex items-center justify-center px-4 py-2 bg-black text-white text-sm font-medium rounded-md hover:bg-gray-900"
          >
            {showAddVilla ? (
              <>
                <X className="h-4 w-4 mr-2" /> Cancel
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" /> Add Villa
              </>
            )}
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-4 py-3">
            {error}
          </div>
        )}

        {showAddVilla && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or owner"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black"
              />
            </div>
            {isLoading ? (
              <div className="flex items-center justify-center py-8 text-gray-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading...
              </div>
            ) : filteredAvailable.length === 0 ? (
              <div className="text-sm text-gray-500 py-6 text-center">
                No villas available to add.
              </div>
            ) : (
              <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {filteredAvailable.map((villa) => (
                  <div
                    key={villa.id}
                    className="border border-gray-200 rounded-lg overflow-hidden flex flex-col"
                  >
                    <div className="w-full h-32 bg-gray-100">
                      <img
                        src={
                          villa.images?.[0] ||
                          villa.thumbnail ||
                          "/placeholder.svg"
                        }
                        alt={villa.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-3 flex flex-col gap-1 flex-1">
                      <div className="text-sm font-semibold text-gray-900 line-clamp-1">
                        {villa.name}
                      </div>
                      <div className="text-xs text-gray-500 line-clamp-1">
                        {villa.owner?.name || "—"}
                      </div>
                      <div className="text-xs text-gray-500">
                        ₹{villa.rate}/night
                      </div>
                      <button
                        type="button"
                        onClick={() => handleAddVilla(villa)}
                        className="mt-2 inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-white bg-black rounded-md hover:bg-gray-900"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500">
                Current Order
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                Drag rows to reorder, or pick a position from the dropdown.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {savingOrder && (
                <span className="inline-flex items-center text-xs text-gray-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  Saving...
                </span>
              )}
              <span className="text-xs text-gray-500">
                {promotedRows.length}{" "}
                {promotedRows.length === 1 ? "villa" : "villas"}
              </span>
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading...
            </div>
          ) : promotedVillas.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg py-16 text-center text-sm text-gray-500">
              No promoted villas yet.
            </div>
          ) : (
            <div className="space-y-2">
              {promotedVillas.map((villa, index) => {
                const isDropTarget = dragOverIdx === index
                return (
                  <div
                    key={villa.id}
                    draggable
                    onDragStart={(e) => {
                      dragFromIdx.current = index
                      e.dataTransfer.effectAllowed = "move"
                    }}
                    onDragOver={(e) => {
                      e.preventDefault()
                      e.dataTransfer.dropEffect = "move"
                      if (dragOverIdx !== index) setDragOverIdx(index)
                    }}
                    onDragLeave={() => {
                      if (dragOverIdx === index) setDragOverIdx(null)
                    }}
                    onDrop={(e) => {
                      e.preventDefault()
                      const from = dragFromIdx.current
                      dragFromIdx.current = null
                      setDragOverIdx(null)
                      moveTo(from, index)
                    }}
                    onDragEnd={() => {
                      dragFromIdx.current = null
                      setDragOverIdx(null)
                    }}
                    className={`bg-white border rounded-lg flex items-center gap-3 p-3 select-none ${
                      isDropTarget
                        ? "border-black ring-1 ring-black/30"
                        : "border-gray-200"
                    }`}
                  >
                    <span
                      className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-700 shrink-0"
                      title="Drag to reorder"
                    >
                      <GripVertical className="h-5 w-5" />
                    </span>
                    <select
                      value={villa.position}
                      onChange={(e) =>
                        handlePositionChange(villa.id, e.target.value)
                      }
                      className="w-16 shrink-0 px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-700 border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black"
                      aria-label="Position"
                    >
                      {promotedVillas.map((_, i) => (
                        <option key={i} value={i + 1}>
                          #{i + 1}
                        </option>
                      ))}
                    </select>
                    <img
                      src={
                        villa.images?.[0] ||
                        villa.thumbnail ||
                        "/placeholder.svg"
                      }
                      alt={villa.name || villa.villaId}
                      className="w-14 h-14 rounded-md object-cover bg-gray-100 shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-gray-900 line-clamp-1">
                        {villa.name || villa.villaId}
                      </div>
                      <div className="text-xs text-gray-500 line-clamp-1">
                        {villa.owner?.name || "—"} ·{" "}
                        {villa.rate ? `₹${villa.rate}/night` : "—"}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveVilla(villa)}
                      className="p-1.5 text-red-600 border border-red-200 rounded-md hover:bg-red-50 shrink-0"
                      aria-label="Remove"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  )
}
